import coreApi from '../api/coreApi';
import type { AuthResponse } from '../types/auth';
import type { BookmarkResponse } from '../types/bookmark';
import type { Verse } from '../types/verse';

export const authService = {
  async signInWithGoogle(): Promise<AuthResponse> {
    try {
      console.log('[Auth Service] Initiating Google sign in');
      const response = await coreApi.get('/auth/google');
      
      if (response.data?.url) {
        // Store the current path to redirect back after auth
        const returnTo = window.location.pathname;
        sessionStorage.setItem('auth_redirect', returnTo);
        
        // Redirect to Google OAuth URL
        window.location.assign(response.data.url);
        return {
          user: null,
          session: null,
          error: null
        };
      }
      
      throw new Error('No OAuth URL received');
    } catch (error: any) {
      console.error('[Auth Service] Google sign in error:', error);
      return {
        user: null,
        session: null,
        error: error.response?.data?.error || new Error('Google sign in failed')
      };
    }
  },

  async handleOAuthCallback(code: string): Promise<AuthResponse> {
    try {
      console.log('[Auth Service] Handling OAuth callback');
      const response = await coreApi.get(`/auth/callback?code=${code}`);
      
      if (response.data?.user) {
        return {
          user: response.data.user,
          session: response.data.session,
          error: null
        };
      }
      
      throw new Error('No user data received');
    } catch (error: any) {
      console.error('[Auth Service] OAuth callback error:', error);
      return {
        user: null,
        session: null,
        error: error.response?.data?.error || new Error('OAuth callback failed')
      };
    }
  },

  async getSession(): Promise<AuthResponse> {
    try {
      console.log('[Auth Service] Checking session');
      const response = await coreApi.get('/auth/session');
      
      if (response.data?.user) {
        return {
          user: response.data.user,
          session: response.data.session,
          error: null
        };
      }
      
      return {
        user: null,
        session: null,
        error: null
      };
    } catch (error: any) {
      console.error('[Auth Service] Session check error:', error);
      return {
        user: null,
        session: null,
        error: error.response?.data?.error || new Error('Session check failed')
      };
    }
  },

  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      console.log('[Auth Service] Attempting login');
      const response = await coreApi.post('/auth/login', { email, password });
      
      if (response.data?.user) {
        return {
          user: response.data.user,
          session: response.data.session,
          error: null
        };
      }
      
      throw new Error('No user data received');
    } catch (error: any) {
      console.error('[Auth Service] Login error:', error);
      return {
        user: null,
        session: null,
        error: error.response?.data?.error || new Error('Login failed')
      };
    }
  },

  async signOut(): Promise<AuthResponse> {
    try {
      console.log('[Auth Service] Signing out');
      const response = await coreApi.post('/auth/signout');
      return {
        user: null,
        session: null,
        error: null
      };
    } catch (error: any) {
      console.error('[Auth Service] Sign out error:', error);
      return {
        user: null,
        session: null,
        error: error.response?.data?.error || new Error('Sign out failed')
      };
    }
  },

  async deleteAccount(): Promise<AuthResponse> {
    try {
      console.log('[Auth Service] Deleting account');
      const response = await coreApi.delete('/auth/account');
      return {
        user: null,
        session: null,
        error: null
      };
    } catch (error: any) {
      console.error('[Auth Service] Delete account error:', error);
      return {
        user: null,
        session: null,
        error: error.response?.data?.error || new Error('Delete account failed')
      };
    }
  },

  async getProfile() {
    try {
      console.log('[Auth Service] Getting profile');
      const response = await coreApi.get('/profile');
      return {
        profile: response.data?.profile || null,
        error: null
      };
    } catch (error: any) {
      console.error('[Auth Service] Get profile error:', error);
      return {
        profile: null,
        error: error.response?.data?.error || new Error('Failed to get profile')
      };
    }
  },

  async updateProfile(updates: any) {
    try {
      console.log('[Auth Service] Updating profile with:', updates);
      const response = await coreApi.put('/profile', updates);
      return {
        profile: response.data?.profile || null,
        error: null
      };
    } catch (error: any) {
      console.error('[Auth Service] Update profile error:', error);
      return {
        profile: null,
        error: error.response?.data?.error || new Error('Failed to update profile')
      };
    }
  },

  async initializeWebSocket() {
    try {
      console.log('[Auth Service] Initializing WebSocket');
      const response = await coreApi.post('/ws/initialize');
      return {
        error: null
      };
    } catch (error: any) {
      console.error('[Auth Service] WebSocket initialization error:', error);
      return {
        error: error.response?.data?.error || new Error('Failed to initialize WebSocket')
      };
    }
  },

  // Bookmark Methods
  async getBookmarks(): Promise<BookmarkResponse> {
    try {
      console.log('[Auth Service] Fetching bookmarks');
      const response = await coreApi.get('/profile/bookmarks');
      console.log('[Auth Service] Bookmarks fetch successful');
      return {
        bookmarks: response.data,
        error: null
      };
    } catch (error: any) {
      console.error('[Auth Service] Bookmarks fetch error:', error);
      return {
        bookmarks: [],
        error: error.response?.data?.error || new Error('Failed to fetch bookmarks')
      };
    }
  },

  async toggleBookmark(verse: Verse, reference: string): Promise<BookmarkResponse> {
    try {
      console.log('[Auth Service] Toggling bookmark');
      const response = await coreApi.post('/profile/bookmarks/toggle', {
        book: verse.book,
        chapter: verse.chapter,
        verse: verse.verse,
        reference
      });
      console.log('[Auth Service] Bookmark toggle successful');
      return {
        bookmarks: response.data,
        error: null
      };
    } catch (error: any) {
      console.error('[Auth Service] Bookmark toggle error:', error);
      return {
        bookmarks: [],
        error: error.response?.data?.error || new Error('Failed to toggle bookmark')
      };
    }
  },

  async deleteBookmark(id: string): Promise<BookmarkResponse> {
    try {
      console.log('[Auth Service] Deleting bookmark');
      const response = await coreApi.delete(`/profile/bookmarks/${id}`);
      console.log('[Auth Service] Bookmark deletion successful');
      return {
        bookmarks: response.data,
        error: null
      };
    } catch (error: any) {
      console.error('[Auth Service] Bookmark deletion error:', error);
      return {
        bookmarks: [],
        error: error.response?.data?.error || new Error('Failed to delete bookmark')
      };
    }
  }

  
};